import React, { useState, useEffect } from 'react';
import { X, FileText, Save } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RCAFormModal = ({ 
  isOpen, 
  onClose, 
  projectId,
  projectTitle,
  onTemplateSaved
}) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    project_title: projectTitle || '',
    objectives: '',
    expected_contributions: '',
    main_researcher_and_collaborators: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && projectId) {
      // Check if template already exists
      fetchExistingTemplate();
    }
  }, [isOpen, projectId]);

  const fetchExistingTemplate = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agreements/rca-template/${projectId}?language=${language}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          setFormData(data.filled_fields);
        }
      }
    } catch (err) {
      console.error('Error fetching template:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validate all fields are filled
    const emptyFields = Object.entries(formData).filter(([key, value]) => !value.trim());
    if (emptyFields.length > 0) {
      setError(t('pleaseFillAllFields') || 'Please fill all fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/create-rca-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: projectId,
          agreement_type: 'RCA',
          language: language,
          filled_fields: formData,
          created_by: '',
          created_at: new Date().toISOString()
        }),
      });

      // Parse response body once
      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error(responseText || 'Failed to parse response');
      }

      // Check for errors after parsing
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to save template');
      }
      
      if (onTemplateSaved) {
        onTemplateSaved(result);
      }

      onClose();
      alert(t('saveRCATemplate') + ' - Success!');

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-purple-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t('fillRCADetails')}
              </h2>
              <p className="text-sm text-gray-500">{projectTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Project Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('projectTitle')} *
              </label>
              <input
                type="text"
                value={formData.project_title}
                onChange={(e) => handleChange('project_title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('projectTitle')}
              />
            </div>

            {/* Research Objectives */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('researchObjectives')} *
              </label>
              <textarea
                value={formData.objectives}
                onChange={(e) => handleChange('objectives', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('researchObjectives')}
              />
            </div>

            {/* Expected Contributions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('expectedContributions')} *
              </label>
              <textarea
                value={formData.expected_contributions}
                onChange={(e) => handleChange('expected_contributions', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('expectedContributions')}
              />
            </div>

            {/* Main Researcher and Collaborators */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('mainResearcherAndCollaborators')} *
              </label>
              <textarea
                value={formData.main_researcher_and_collaborators}
                onChange={(e) => handleChange('main_researcher_and_collaborators', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('mainResearcherAndCollaborators')}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
              !saving
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save className="h-5 w-5" />
            {saving ? t('saving') : t('saveRCATemplate')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RCAFormModal;
